import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, ArrowRight, Clock, Sparkles, Radio, Loader2, Search, X } from "lucide-react";
import Seo from "@/components/Seo";
import { AdBanner } from "@/components/AdBanner";
import { ARTICLES } from "@/data/articles";
import api from "@/lib/api";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function News() {
  const [data, setData] = useState(null);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const runSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/news/search`, { params: { q: query.trim() } });
      setResults(data.items);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };
  const clearSearch = () => { setQuery(""); setResults(null); };

  useEffect(() => {
    setLoading(true);
    api.get(`/news?limit=40${source ? `&source=${encodeURIComponent(source)}` : ""}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ items: [], sources: [] }))
      .finally(() => setLoading(false));
  }, [source]);

  const items = data?.items || [];
  const [featured, ...rest] = items;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-12" data-testid="news-page">
      <Seo title="Gaming News — Live, AI-Synthesized & Multi-Perspective" description="A living feed of gaming news pulled from 17+ top outlets, with AI briefs and multi-perspective 'lens' rewrites. See how every story develops over time." />

      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <Newspaper className="w-9 h-9 text-neon-cyan" />
        <h1 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tighter">Gaming News</h1>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neon-pink/15 border border-neon-pink/50 text-neon-pink text-[11px] font-bold uppercase tracking-widest">
          <Radio className="w-3 h-3 animate-pulse" /> Live
        </span>
      </div>
      <p className="text-zinc-400 max-w-2xl mb-2">A living feed from 17+ top outlets — each story gets an AI brief, a developing timeline, and <span className="text-neon-cyan">multi-perspective lens</span> rewrites.</p>
      {data?.last_updated && (
        <p className="text-xs text-zinc-600 font-mono mb-8 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Feed updated {timeAgo(data.last_updated)}
        </p>
      )}

      {/* source filter */}
      {data?.sources?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="news-sources">
          <button onClick={() => setSource("")} data-testid="news-source-all"
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${source === "" ? "bg-neon-cyan text-black border-neon-cyan" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-white"}`}>
            All
          </button>
          {data.sources.map((s) => (
            <button key={s} onClick={() => setSource(s)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-full border-2 transition-colors ${source === s ? "bg-neon-cyan text-black border-neon-cyan" : "bg-transparent text-zinc-300 border-zinc-700 hover:border-white"}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* semantic search */}
      <form onSubmit={runSearch} className="relative max-w-xl mb-8" data-testid="news-search-form">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="news-search-input"
          placeholder="Search stories by meaning, e.g. 'PlayStation exclusives'…"
          className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-neon-cyan outline-none rounded-full pl-11 pr-36 py-3 text-white" />
        {results !== null && (
          <button type="button" onClick={clearSearch} data-testid="news-search-clear"
            className="absolute right-[6.75rem] top-1/2 -translate-y-1/2 z-10 text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        )}
        <button type="submit" disabled={searching || query.trim().length < 2} data-testid="news-search-submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-neon-cyan text-black font-bold uppercase text-xs tracking-widest rounded-full px-4 py-2 disabled:opacity-50">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
        </button>
      </form>

      {results !== null ? (
        <div data-testid="news-search-results">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-neon-cyan" />
            <h2 className="font-display font-bold uppercase tracking-widest text-sm text-zinc-400">{results.length} AI-matched {results.length === 1 ? "story" : "stories"} for "{query}"</h2>
          </div>
          {results.length === 0 ? (
            <p className="text-zinc-500 font-mono py-10">No matches. Try different words.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {results.map((a, i) => (
                <Link key={a.id} to={`/news/story/${a.id}`} data-testid={`search-result-${i}`}
                  className="group block bg-zinc-950 border-2 border-zinc-800 hover:border-neon-cyan rounded-xl overflow-hidden h-full transition-colors">
                  <div className="relative aspect-video overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/80 text-neon-cyan border border-neon-cyan rounded">{a.source}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-bold text-base uppercase tracking-tight leading-tight group-hover:text-neon-cyan transition-colors line-clamp-3">{a.title}</h3>
                    <span className="text-xs text-zinc-600 font-mono mt-3 block">{timeAgo(a.published)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : loading ? (
        <div className="py-24 flex flex-col items-center gap-3 text-zinc-400" data-testid="news-loading">
          <Loader2 className="w-7 h-7 animate-spin text-neon-cyan" />
          <p className="font-mono text-sm">Loading the latest…</p>
        </div>
      ) : items.length === 0 ? (
        <p className="text-zinc-500 text-center py-20 font-mono" data-testid="news-empty">No stories yet — the feed is warming up. Check back shortly.</p>
      ) : (
        <>
          {/* Featured */}
          <Link to={`/news/story/${featured.id}`} data-testid="story-featured"
            className="group grid md:grid-cols-2 gap-0 rounded-2xl border-2 border-zinc-800 hover:border-neon-cyan overflow-hidden mb-10 transition-colors">
            <div className="relative aspect-video md:aspect-auto overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <div className="p-7 md:p-10 flex flex-col justify-center">
              <span className="inline-flex w-fit items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-neon-cyan text-black rounded mb-3">{featured.source}</span>
              <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight group-hover:text-neon-cyan transition-colors">{featured.title}</h2>
              <p className="text-zinc-400 mt-3 line-clamp-3">{featured.excerpt}</p>
              <div className="flex items-center gap-3 mt-4 text-xs text-zinc-500 font-mono">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {timeAgo(featured.published)}</span>
              </div>
              <span className="inline-flex items-center gap-1 mt-5 text-neon-cyan font-bold uppercase text-sm tracking-widest">
                Read with AI lenses <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {rest.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 8) * 0.05 }}>
                <Link to={`/news/story/${a.id}`} data-testid={`story-card-${i}`}
                  className="group block bg-zinc-950 border-2 border-zinc-800 hover:border-neon-cyan rounded-xl overflow-hidden h-full transition-colors">
                  <div className="relative aspect-video overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <span className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/80 text-neon-cyan border border-neon-cyan rounded">{a.source}</span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-bold text-base uppercase tracking-tight leading-tight group-hover:text-neon-cyan transition-colors line-clamp-3">{a.title}</h3>
                    <span className="text-xs text-zinc-600 font-mono mt-3 block">{timeAgo(a.published)}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </>
      )}

      <AdBanner slot="news" className="mt-12" />

      {/* Static deep dives / SEO guides */}
      <section className="mt-16">
        <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight mb-1">Deep Dives & Guides</h2>
        <p className="text-zinc-500 text-sm mb-6">Evergreen explainers on what's shaping gaming right now.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ARTICLES.map((a) => (
            <Link key={a.slug} to={`/news/${a.slug}`} data-testid={`article-card-${a.slug}`}
              className="group block bg-zinc-950 border-2 border-zinc-800 hover:border-neon-lime rounded-xl overflow-hidden h-full transition-colors">
              <div className="relative aspect-video overflow-hidden">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <span className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-black/80 text-neon-lime border border-neon-lime rounded">{a.category}</span>
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-lg uppercase tracking-tight leading-tight group-hover:text-neon-lime transition-colors">{a.title}</h3>
                <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
